/** Mockup 02 · Etapa 1 concluída — A aguardando WhatsApp · B já em atendimento · C funil sem canal. */
import { DEFAULT_GAMIFICATION_RULES, type LeadCaptureSession } from '@troia-v3/types';
import React from 'react';

import { Button, ModalContent, ModalFooter } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { formatPhoneNational } from '@/shared/utils/phone';

import { CaptureStepper, buildCaptureSteps } from './CaptureStepper';
import { CaptureDoneHead, CaptureInfoBox, CaptureKv, CapturePointsPill } from './captureUi';

export interface CaptureStepActions {
  session: LeadCaptureSession;
  onCancel: () => void;
  onSkip: () => void;
  onContinue: () => void;
  busy: boolean;
}

const capturePoints = DEFAULT_GAMIFICATION_RULES.find((r) => r.key === 'lead_captured')?.points ?? 1;

/** `/crm/{funnelId}?leadId={leadId}` — URL params documentados em `CrmDashboardPage.tsx`. */
export const leadHref = (session: LeadCaptureSession): string => `/crm/${session.funnelId}?leadId=${session.leadId ?? ''}`;
const formatDay = (iso: string | undefined): string => (iso ? new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '');

export const CaptureFilledStep: React.FC<CaptureStepActions> = ({ session, onCancel, onSkip, onContinue, busy }) => {
  const hasChannel = Boolean(session.capture.channelId);
  // `formCompletedAt` já setado (sessão retomada em 'form_completed') não pode oferecer "Pular formulário" de novo — backend rejeita.
  const hasForm = Boolean(session.capture.formId) && !session.formCompletedAt;
  const steps = buildCaptureSteps(hasChannel, hasForm);
  const customer = session.customer;
  const phone = customer ? formatPhoneNational(customer.phone) : '';
  const variant = session.wasExistingLead ? 'existing' : hasChannel ? 'waiting-whatsapp' : 'no-channel';

  // ── B · já em atendimento com outro vendedor (D5) ───────────────────────
  if (variant === 'existing') {
    return (
      <>
        <ModalContent>
          <div data-testid="capture-filled">
            <CaptureStepper steps={steps} current={1} />
            <CaptureInfoBox tone="amber" icon="alert-triangle" className="mb-3 !text-[13px]" testId="capture-filled-variant" dataVariant="existing">
              <b>Este cliente já está em atendimento.</b> {customer?.name} tem um lead aberto neste funil com <b>{session.existingAssigneeName ?? 'outro vendedor'}</b>. Pela regra do funil, o lead não foi duplicado e esta captura não conta pontos.
            </CaptureInfoBox>
            <CaptureKv rows={[
              { k: 'Nome', v: customer?.name ?? '—' },
              { k: 'WhatsApp', v: phone, mono: true },
              { k: 'Responsável atual', v: session.existingAssigneeName ?? '—' },
              { k: 'No funil desde', v: `${formatDay(session.existingLeadCreatedAt)}${session.existingLeadStepName ? ` · etapa ${session.existingLeadStepName}` : ''}` },
            ]} />
            <div className="text-xs mt-3" style={{ color: '#64748b' }}>Se o cliente quiser trocar de vendedor, peça ao gestor para transferir o lead.</div>
          </div>
        </ModalContent>
        <ModalFooter>
          <span className="flex-1" />
          <Button variant="link" icon="external-link" onClick={() => window.open(leadHref(session), '_blank')} data-testid="capture-open-lead">Abrir lead</Button>
          <Button variant="ghost" loading={busy} onClick={onSkip} data-testid="capture-skip">Encerrar</Button>
        </ModalFooter>
      </>
    );
  }

  const kv = (
    <CaptureKv rows={[
      { k: 'Nome', v: customer?.name ?? '—' },
      { k: 'WhatsApp', v: phone, mono: true },
      { k: 'Lead', v: (
        <span className="inline-flex items-center gap-1.5">
          {session.funnelName} › {session.stepName}
          <a href={leadHref(session)} target="_blank" rel="noreferrer" className="inline-flex" style={{ color: '#7c3aed' }} data-testid="capture-open-lead"><Icon name="external-link" size={14} /></a>
        </span>
      ) },
    ]} />
  );

  // ── C · funil sem canal: captura já confirmada ──────────────────────────
  if (variant === 'no-channel') {
    return (
      <>
        <ModalContent>
          <div data-testid="capture-filled">
            <CaptureStepper steps={steps} current={steps.length - 1} />
            <CaptureDoneHead tone="green" icon="check" title="Etapa 1 concluída">O cliente preencheu os dados. Este funil não pede confirmação no WhatsApp.</CaptureDoneHead>
            {kv}
            <div className="flex items-center gap-2.5 mt-3 flex-wrap">
              <CapturePointsPill testId="capture-filled-variant" dataVariant="no-channel">+{capturePoints} ponto · captura confirmada</CapturePointsPill>
              {hasForm && <span className="text-xs" style={{ color: '#64748b' }}>Próximo: formulário de captura (≈ 1 min)</span>}
            </div>
          </div>
        </ModalContent>
        <ModalFooter>
          <Button variant="ghost" icon="x" loading={busy} onClick={onCancel} data-testid="capture-filled-cancel">Cancelar</Button>
          <span className="flex-1" />
          {hasForm ? (
            <>
              <Button variant="link" loading={busy} onClick={onSkip} data-testid="capture-skip">Pular formulário</Button>
              <Button variant="primary" icon="arrow-right" iconPosition="right" loading={busy} onClick={onContinue} data-testid="capture-continue">Continuar atendimento</Button>
            </>
          ) : (
            <Button variant="primary" icon="arrow-right" iconPosition="right" loading={busy} onClick={onContinue} data-testid="capture-continue">Continuar atendimento</Button>
          )}
        </ModalFooter>
      </>
    );
  }

  // ── A · aguardando confirmação no WhatsApp ──────────────────────────────
  return (
    <>
      <ModalContent>
        <div data-testid="capture-filled">
          <CaptureStepper steps={steps} current={2} />
          <CaptureDoneHead tone="green" icon="check" title="Etapa 1 concluída">O cliente preencheu os dados agora há pouco. O lead já está no funil com você.</CaptureDoneHead>
          {kv}
          <div className="mt-3 rounded-xl flex gap-3 items-start" style={{ border: '1px solid #c9f0d7', background: '#f0fdf4', padding: '12px 14px' }} data-testid="capture-filled-variant" data-variant="waiting-whatsapp">
            <div className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-none text-white" style={{ background: '#25d366' }}><Icon name="message-circle" size={20} /></div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-[13px]" style={{ color: '#14532d' }}>Aguardando confirmação no WhatsApp</div>
              <div className="text-xs mt-0.5 leading-[1.45]" style={{ color: '#166534' }}>
                O cliente precisa enviar a mensagem que já abriu no celular dele. Quando ela chegar no canal <b>{session.capture.channelName ?? 'configurado'}</b>, a captura é confirmada e esta tela avança.
              </div>
              <div className="flex items-center gap-3 flex-wrap mt-2">
                <span className="inline-flex items-center rounded-[7px] text-[13px] font-semibold" style={{ background: '#dcfce7', color: '#14532d', padding: '4px 9px', fontVariantNumeric: 'tabular-nums' }} data-testid="capture-filled-code">Código {session.code}</span>
                <span className="inline-flex items-center gap-1.5 text-xs" style={{ color: '#166534' }}>
                  <span className="w-3.5 h-3.5 rounded-full border-2 animate-spin motion-reduce:animate-none inline-block" style={{ borderColor: '#bbf7d0', borderTopColor: '#16a34a' }} />
                  verificando…
                </span>
              </div>
            </div>
          </div>
        </div>
      </ModalContent>
      <ModalFooter>
        <Button variant="ghost" icon="x" loading={busy} onClick={onCancel} data-testid="capture-filled-cancel">Cancelar</Button>
        <span className="flex-1" />
        <Button variant="link" loading={busy} onClick={onSkip} data-testid="capture-skip">Pular confirmação</Button>
        <Button variant="primary" icon="arrow-right" iconPosition="right" disabled data-testid="capture-continue">Continuar atendimento</Button>
      </ModalFooter>
    </>
  );
};
